import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiArrowLeft, FiLoader, FiTrash2, FiCopy, FiCheck } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { keysApisAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  font-family: ${props => props.$monospace ? "'Courier New', monospace" : "inherit"};
  transition: all 0.2s ease;
  resize: vertical;
  ${props => props.$rows 
    ? `min-height: ${props.$rows * 24 + 24}px; height: ${props.$rows * 24 + 24}px;`
    : 'min-height: 60px; height: 60px;'
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$danger || props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$danger || props.$primary) return theme.colors.text.primary;
      return theme.colors.primary[500];
    }};
    border-color: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.primary[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.error[600]};
  font-size: ${theme.fontSizes.sm};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
`;

const HelpText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  margin: 0;
  margin-top: ${theme.spacing[1]};
`;

const InputGroup = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  align-items: stretch;
`;

const InputWithButton = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CopyButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  min-width: 100px;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[500]};
    color: ${theme.colors.text.primary};
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Modal de confirmação
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const KeyApiForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id && id !== 'novo';

  const [formData, setFormData] = useState({
    api: '',
    key: '',
    descricao: ''
  });

  const [originalKey, setOriginalKey] = useState(null); // Armazena a key real quando está editando
  const [keyModified, setKeyModified] = useState(false); // Indica se a key foi modificada
  const [copied, setCopied] = useState(false); // Indica se a key foi copiada

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (isEditing) {
      carregarKey();
    }
  }, [id]);

  const carregarKey = async () => {
    try {
      setLoadingData(true);
      const response = await keysApisAPI.buscarPorId(id);
      
      if (response.data.success) {
        const keyValue = response.data.data.key || '';
        setOriginalKey(keyValue); // Armazena a key real
        // Mostra "*" para cada caractere da key quando está editando
        const maskedKey = keyValue ? '*'.repeat(keyValue.length) : '';
        setFormData({
          api: response.data.data.api || '',
          key: maskedKey,
          descricao: response.data.data.descricao || ''
        });
        setKeyModified(false); // A key ainda não foi modificada
      } else {
        setError('Erro ao carregar dados da API Key');
      }
    } catch (err) {
      console.error('Erro ao carregar API Key:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'key' && isEditing && !keyModified && originalKey) {
      // Se está editando e a key ainda não foi modificada, verificar se foi realmente modificada
      // A key foi modificada se o tamanho mudou ou se algum caractere não é "*"
      const isStillMasked = value.length === originalKey.length && 
        value.split('').every(char => char === '*');
      
      if (!isStillMasked) {
        // Se não está mais mascarada, marcar como modificada
        setKeyModified(true);
      }
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCopyKey = async () => {
    if (!originalKey) return;
    
    try {
      await navigator.clipboard.writeText(originalKey);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Erro ao copiar key:', err);
      setError('Erro ao copiar a chave para a área de transferência');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Validações
      if (!formData.api.trim()) {
        throw new Error('Campo "API" é obrigatório');
      }
      
      // Se está editando e a key não foi modificada, usar a key original
      // Verifica se a key atual é apenas asteriscos (key mascarada)
      const isMaskedKey = isEditing && !keyModified && originalKey && 
        formData.key.length === originalKey.length && 
        formData.key.split('').every(char => char === '*');
      
      const keyToSave = isMaskedKey 
        ? originalKey 
        : formData.key.trim();
      
      if (!keyToSave || !keyToSave.trim()) {
        throw new Error('Campo "Key" é obrigatório');
      }

      // Preparar dados para envio
      // Sempre enviar descricao, mesmo se vazio (será null)
      const descricaoEnvio = formData.descricao && formData.descricao.trim() !== '' 
        ? formData.descricao.trim() 
        : null;
      
      const dadosParaEnvio = {
        api: formData.api.trim(),
        key: keyToSave,
        descricao: descricaoEnvio
      };
      
      console.log('Dados para envio:', {
        ...dadosParaEnvio,
        key: keyToSave ? `${keyToSave.substring(0, 10)}...` : 'vazio'
      });

      let response;
      if (isEditing) {
        response = await keysApisAPI.atualizar(id, dadosParaEnvio);
      } else {
        response = await keysApisAPI.criar(dadosParaEnvio);
      }

      if (response.data.success) {
        setSuccess(isEditing ? 'API Key atualizada com sucesso!' : 'API Key criada com sucesso!');
        setTimeout(() => {
          navigate('/administracao/keys-apis');
        }, 1500);
      } else {
        setError(response.data.error || 'Erro ao salvar API Key');
      }
    } catch (err) {
      console.error('Erro ao salvar API Key:', err);
      
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Erro ao conectar com o servidor. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/administracao/keys-apis');
  };

  // Função para excluir API Key
  const handleExcluirKey = async () => {
    try {
      setDeleting(true);
      setError(null);
      setSuccess(null);

      const response = await keysApisAPI.deletar(id);
      
      if (response.data && response.data.success) {
        setSuccess('API Key excluída com sucesso!');
        setShowDeleteModal(false);
        setTimeout(() => {
          navigate('/administracao/keys-apis');
        }, 1000);
      } else {
        setError(response.data?.error || 'Erro ao excluir API Key');
        setDeleting(false);
        setShowDeleteModal(false);
      }
    } catch (err) {
      console.error('Erro ao excluir API Key:', err);
      
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Erro ao conectar com o servidor. Tente novamente.');
      }
      
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Função para abrir modal de confirmação
  const handleAbrirModalExclusao = () => {
    setShowDeleteModal(true);
  };

  // Função para cancelar exclusão
  const handleCancelarExclusao = () => {
    setShowDeleteModal(false);
  };

  if (loadingData) {
    return (
      <Layout title="Carregando...">
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <FiLoader style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }} />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Editar API Key' : 'Nova API Key'}>
      <Container>
        <Header>
          <BackButton onClick={handleVoltar}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>
            {isEditing ? 'Editar API Key' : 'Nova API Key'}
          </Title>
        </Header>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormGroup>
            <Label htmlFor="api">
              API <Required>*</Required>
            </Label>
            <Input
              id="api"
              type="text"
              placeholder="Ex: WhatsApp, N8N, Integração Externa"
              value={formData.api}
              onChange={(e) => handleInputChange('api', e.target.value)}
              required
            />
            <HelpText>Nome identificador da API que utilizará esta chave</HelpText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="descricao">
              Descrição / Finalidade
            </Label>
            <Textarea
              id="descricao"
              placeholder="Ex: Chave para autenticação na API externa de créditos, usada para registrar consumo de créditos dos clientes"
              value={formData.descricao}
              onChange={(e) => handleInputChange('descricao', e.target.value)}
              $rows={3}
            />
            <HelpText>Descreva para que esta chave será usada (opcional)</HelpText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="key">
              Key <Required>*</Required>
            </Label>
            <InputGroup>
              <InputWithButton>
                <Textarea
                  id="key"
                  placeholder={isEditing && !keyModified ? "Clique em 'Copiar' para ver a chave ou edite para alterá-la" : "Cole ou digite a chave de API aqui"}
                  value={formData.key}
                  onChange={(e) => handleInputChange('key', e.target.value)}
                  required
                  $monospace
                />
              </InputWithButton>
              {isEditing && originalKey && (
                <CopyButton
                  type="button"
                  onClick={handleCopyKey}
                  disabled={loading || loadingData}
                  title="Copiar chave para a área de transferência"
                >
                  {copied ? (
                    <>
                      <FiCheck />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <FiCopy />
                      Copiar
                    </>
                  )}
                </CopyButton>
              )}
            </InputGroup>
            <HelpText>
              {isEditing && !keyModified
                ? "A chave está oculta por segurança. Use o botão 'Copiar' para copiar a chave ou edite o campo para alterá-la."
                : "A chave será usada para autenticação no header X-API-Key"}
            </HelpText>
          </FormGroup>

          <Actions>
            <Button type="button" onClick={handleVoltar}>
              Cancelar
            </Button>
            {isEditing && (
              <Button type="button" onClick={handleAbrirModalExclusao} $danger>
                <FiTrash2 />
                Excluir
              </Button>
            )}
            <Button $primary type="submit" disabled={loading}>
              {loading ? <FiLoader /> : <FiSave />}
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Actions>
        </Form>
      </Container>

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Excluir API Key</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir esta API Key? Esta ação não pode ser desfeita e irá excluir:
              <br /><br />
              • A API Key <strong>{formData.api}</strong>
              <br />
              • Todos os dados relacionados
              <br /><br />
              <strong>Atenção:</strong> Após a exclusão, esta chave não poderá mais ser usada para autenticação em APIs externas.
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={handleCancelarExclusao}>
                Cancelar
              </ModalButton>
              <ModalButton $danger onClick={handleExcluirKey} disabled={deleting}>
                <FiTrash2 />
                {deleting ? 'Excluindo...' : 'Excluir'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default KeyApiForm;

